import * as React from "react";

import { cn } from "../../lib/utils";

type SkeletonVariant = "rect" | "text" | "title" | "circle";
type SkeletonAnimation = "pulse" | "none";
type SkeletonRounded = "none" | "small" | "medium" | "large" | "full";

const variantDefaults: Record<
  SkeletonVariant,
  { height: number | string; className: string }
> = {
  rect: { height: 16, className: "w-full rounded-large" },
  text: { height: 12, className: "w-full rounded-large" },
  title: { height: 20, className: "w-full rounded-large" },
  circle: { height: 40, className: "rounded-full aspect-square" },
};

const roundedClassNames: Record<SkeletonRounded, string> = {
  none: "rounded-none",
  small: "rounded-small",
  medium: "rounded-medium",
  large: "rounded-large",
  full: "rounded-full",
};

const animationClassNames: Record<SkeletonAnimation, string | undefined> = {
  pulse: "animate-skeleton",
  none: undefined,
};

const normalizeCssValue = (value?: number | string): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  return typeof value === "number" ? `${value}px` : value;
};

const getLineWidths = (
  count: number,
  provided?: Array<number | string>
): Array<string | undefined> => {
  if (provided && provided.length) {
    return Array.from({ length: count }, (_, index) =>
      normalizeCssValue(provided[index] ?? provided[provided.length - 1])
    );
  }

  if (count === 1) {
    return ["100%"];
  }

  return Array.from({ length: count }, (_, index) =>
    index === count - 1 ? "60%" : "100%"
  );
};

export interface SkeletonProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /**
   * Visual variant that controls default sizing and shape.
   */
  variant?: SkeletonVariant;
  /**
   * Animation style applied to the skeleton.
   */
  animation?: SkeletonAnimation;
  /**
   * Number of lines to render for text-based skeletons.
   */
  lines?: number;
  /**
   * Optional width overrides for each line.
   */
  lineWidths?: Array<number | string>;
  /**
   * Gap between lines when rendering multiple.
   */
  lineGap?: number | string;
  /**
   * Width of the skeleton block (overrides the variant default).
   */
  width?: number | string;
  /**
   * Height of the skeleton block (overrides the variant default).
   */
  height?: number | string;
  /**
   * Additional class name applied to the container when multiple lines are rendered.
   */
  containerClassName?: string;
  /**
   * Optional inline styles for the container when rendering multiple lines.
   */
  containerStyle?: React.CSSProperties;
  /**
   * Border radius override for rectangular variants.
   */
  rounded?: SkeletonRounded;
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      className,
      containerClassName,
      containerStyle,
      variant = "rect",
      animation = "pulse",
      lines = 1,
      lineWidths,
      lineGap = "0.5rem",
      width,
      height,
      rounded = "large",
      style,
      ...props
    },
    ref
  ) => {
    const resolvedLines = Math.max(1, lines);
    const baseVariant = variantDefaults[variant];

    const resolvedHeight =
      normalizeCssValue(height) ?? normalizeCssValue(baseVariant.height);
    const resolvedWidth =
      normalizeCssValue(width) ??
      (variant === "circle" ? resolvedHeight : undefined);

    const skeletonClasses = cn(
      "block bg-surface-fill-neutral-subtle",
      animationClassNames[animation],
      variant !== "circle" && roundedClassNames[rounded],
      baseVariant.className,
      className
    );

    if ((variant === "text" || variant === "title") && resolvedLines > 1) {
      const widths = getLineWidths(resolvedLines, lineWidths);

      return (
        <div
          ref={ref}
          className={cn(
            "flex w-full flex-col",
            containerClassName,
            animation === "pulse" ? "animate-none" : undefined
          )}
          style={{
            gap: normalizeCssValue(lineGap) ?? "0.5rem",
            ...containerStyle,
          }}
          aria-live="polite"
          aria-busy="true"
          {...props}
        >
          {widths.map((lineWidth, index) => (
            <span
              key={`skeleton-line-${index}`}
              className={skeletonClasses}
              style={{
                height: resolvedHeight,
                width: lineWidth ?? resolvedWidth ?? "100%",
                ...style,
              }}
              aria-hidden="true"
            />
          ))}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={skeletonClasses}
        style={{
          height: resolvedHeight,
          width: resolvedWidth ?? style?.width,
          ...style,
        }}
        aria-live="polite"
        aria-busy="true"
        {...props}
      />
    );
  }
);

Skeleton.displayName = "Skeleton";

export { Skeleton };
